import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { queryClient } from '@/http/query-client';
import {
  setAppRegistry,
  mergeAppRegistry,
  appIdFromRoutePath,
  APP_TYPE_EXTERNAL,
  type AppLoadMeta,
} from './app-registry';
import { ensureRoleScreens } from '@/auth/role-screen-access';

/**
 * App-launcher tiles — the real, per-client app feed shown in the Apps page and
 * the header Apps flyout.
 *
 * Faithful port of legacy `HeaderWorkspacesService.loadMyApps(clientId)`
 * (raptor `shell-header/src/app/modules/header-workspaces`):
 *   1. `GET /portalapps/api/v1/Apps/GetTiles?clientId&moduleName=Portal` → tiles
 *   2. `GET /role-scr-mgmt/api/v1/RoleScreenMapping/GetRolesMapping?source=Portal`
 *      → the screens the signed-in user may open
 *   3. keep tiles whose `tileRoutePath` has a matching role screen, dropping
 *      header-only / SHD / chatbot app types and the orchestrator workspace tile.
 *
 * Unlike legacy (which `zip`s both calls and shows nothing if either errors),
 * the role mapping here is best-effort: if it fails we render the client tiles
 * unfiltered rather than collapsing the whole launcher to empty.
 */

// App type ids — legacy `shell-header/src/app/core/enum/app-types.ts`.
const APP_TYPE = {
  MFE: 1,
  PortalHeader: 2,
  Iframe: 3,
  External: 4,
  SHD: 5,
  ChatBot: 6,
} as const;

// Tiles the launcher never shows (legacy `HeaderWorkspacesComponent` filter +
// `HeaderWorkspacesService.TILE_HIDE_LIST_REG`).
const HIDDEN_APP_TYPES: ReadonlySet<number> = new Set([
  APP_TYPE.PortalHeader,
  APP_TYPE.SHD,
  APP_TYPE.ChatBot,
]);
const TILE_HIDE_RE = /workspace\/orchestrator/;

const CDN_BASE = import.meta.env.VITE_CDN_IMAGES_BASE_URL ?? '/static/shell-images/shell-images/';
const appRegistryCache = new Map<string, AppLoadMeta[]>();

export interface AppTileDto {
  tileID: number;
  tileDisplayName: string;
  sectionName?: string;
  tileImage?: string;
  tileRoutePath: string;
  tileDescription?: string;
  externalRoutePath?: string;
  appTypeID: number;
}

export interface AppTileView {
  id: number;
  /** Mount appId — final segment of `tileRoutePath`, lowercased. */
  appId: string;
  name: string;
  description: string;
  iconUrl: string;
  /** MFE route path (internal) or external URL, per `appTypeID`. */
  target: string;
  /** Legacy `appTypeID` — drives inline vs iframe vs external mounting. */
  appTypeID: number;
  externalRoutePath?: string;
  isExternal: boolean;
  isFavorite: boolean;
}

/**
 * Build a tile icon URL the way the workspace MFE launcher does: prepend the
 * shell-images base to the raw `tileImage`. The SVGs are self-contained
 * coloured tiles (the colour square + glyph are baked in), so unlike the legacy
 * *header* apps panel we do NOT swap in the white `_headerapps` variant.
 * Returns '' when no image is set so the UI can fall back to a generic icon.
 */
function tileIconUrl(tileImage?: string): string {
  if (!tileImage) return '';
  return `${CDN_BASE}${tileImage}`;
}

function mapTile(tile: AppTileDto): AppTileView {
  const isExternal = tile.appTypeID === APP_TYPE_EXTERNAL;
  return {
    id: tile.tileID,
    appId: appIdFromRoutePath(tile.tileRoutePath),
    name: tile.tileDisplayName,
    description: tile.tileDescription ?? '',
    iconUrl: tileIconUrl(tile.tileImage),
    target: isExternal ? (tile.externalRoutePath ?? '') : tile.tileRoutePath,
    appTypeID: tile.appTypeID,
    externalRoutePath: tile.externalRoutePath,
    isExternal,
    isFavorite: tile.sectionName === 'Favorite Apps',
  };
}

/**
 * Per-client sets of raw `tileRoutePath`s (lowercased) for the tile-existence
 * gate (legacy `AuthorizeService.isTileExistsInClient`). Built from the RAW tile
 * feed (NOT the hidden-type-filtered set) to mirror legacy `getApps`/`getAdminApps`
 * `.some(...)`, which scans every returned (already-active) tile.
 */
const workspaceTilePathsByClient = new Map<string, ReadonlySet<string>>();
const adminTilePathsByClient = new Map<string, ReadonlySet<string>>();

const WORKSPACE_SCREEN_PREFIX = 'workspace/apps';
const ADMIN_SCREEN_PREFIX = 'admin/apps';
const POPUP_SCREEN_PREFIX = 'popup:apps';

function tilePathSet(tiles: AppTileDto[]): ReadonlySet<string> {
  return new Set(tiles.map((t) => (t.tileRoutePath ?? '').toLowerCase()).filter(Boolean));
}

/**
 * Layer 2 access gate — port of legacy `AuthorizeService.isTileExistsInClient`.
 * Even when the role-screen mapping grants a screen (Layer 1), the app must also
 * be PROVISIONED + active for the client. Only `workspace/apps/*`, `popup:apps*`
 * and `admin/apps/*` screen paths are checked against the corresponding tile
 * feed; everything else (bare `home`, `workspace`, `mad`/`pie`, …) short-circuits
 * to allowed, mirroring legacy `else → true`. Match is `tileRoutePath` CONTAINS
 * the screen path, case-insensitive (legacy `indexOf(url) > -1`).
 *
 * Degrades gracefully: an unloaded / empty feed does NOT block — the feed is
 * warmed by `ensureAppRegistry`/`ensureAdminAppRegistry` in the same route
 * `beforeLoad`, but a failed fetch must not wall off the shell (as with Layer 1).
 */
export function isTileProvisioned(clientId: string, screenPath: string): boolean {
  const url = screenPath.trim().toLowerCase();
  if (!url) return true;

  let paths: ReadonlySet<string> | undefined;
  if (url.startsWith(WORKSPACE_SCREEN_PREFIX) || url.startsWith(POPUP_SCREEN_PREFIX)) {
    paths = workspaceTilePathsByClient.get(clientId);
  } else if (url.startsWith(ADMIN_SCREEN_PREFIX)) {
    paths = adminTilePathsByClient.get(clientId);
  } else {
    return true;
  }

  if (!paths || paths.size === 0) return true;
  return [...paths].some((p) => p.includes(url));
}

/** Shared query key for the raw GetTiles feed (see {@link ensureRawTiles}). */
export const RAW_TILES_QUERY_KEY = 'raw-tiles';

/**
 * Fetch the raw GetTiles feed once per client and cache it. Every consumer — the
 * app launcher (`useAppTiles`), the route-loader registry warm-up
 * (`ensureAppRegistry`), and header icon visibility — goes through here, so the
 * feed is fetched ONCE per client even when the callers run at DIFFERENT times
 * (`fetchQuery` returns the cached feed while it's fresh, and also dedups
 * concurrent callers). Explicit refreshes (client switch, Apps-panel reload for
 * fresh favorites) bust `['raw-tiles', clientId]` to force a re-fetch.
 */
export function ensureRawTiles(clientId: string): Promise<AppTileDto[]> {
  return queryClient.fetchQuery({
    queryKey: [RAW_TILES_QUERY_KEY, clientId],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const raw = await http<AppTileDto[] | { tiles?: AppTileDto[]; data?: AppTileDto[] }>(
        Endpoints.appTiles(clientId),
      );
      // The endpoint returns a bare array, but tolerate a wrapped shape too.
      return Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.tiles)
          ? raw.tiles
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
    },
  });
}

async function fetchAppTiles(clientId: string): Promise<AppTileView[]> {
  const tiles = await ensureRawTiles(clientId);

  // Cache raw tile paths for the Layer 2 tile-existence gate (isTileProvisioned).
  workspaceTilePathsByClient.set(clientId, tilePathSet(tiles));

  // Best-effort role filter (see file header). Only applied when the role
  // service returns a NON-EMPTY screen list — an empty/failed mapping must NOT
  // wipe out every tile (that would render an empty launcher even though the
  // user has apps). Legacy hard-zips both calls; we degrade gracefully instead.
  // Uses the shared `ensureRoleScreens` to deduplicate with the route guard,
  // SideNavRail, and header icon visibility — one HTTP call for all consumers.
  let allowedScreens: ReadonlySet<string> | null = null;
  try {
    const screens = await ensureRoleScreens(clientId);
    allowedScreens = screens.length > 0 ? new Set(screens) : null;
  } catch {
    allowedScreens = null;
  }

  // Tiles eligible to MOUNT — hide only the non-app types (header/SHD/chatbot)
  // and the orchestrator workspace tile. The ROLE filter is deliberately NOT
  // applied here: it governs what the launcher UI *shows*, but the load-type
  // registry must know how to mount EVERY app the backend returned (e.g. an
  // app reachable from the embedded workspace launcher, or a deep-link) — else
  // an app missing from the role list would fall back to the inline legacy
  // loader and fail (ChunkLoadError) instead of using its correct loader.
  const loadableTiles = tiles
    .filter((t) => !HIDDEN_APP_TYPES.has(t.appTypeID))
    .filter((t) => !TILE_HIDE_RE.test(t.tileRoutePath ?? ''));

  // Populate the app load-registry from the broad loadable set so the MFE host
  // knows how to mount each app (inline vs iframe vs external) — the same
  // per-client feed the legacy shell uses in `resolveIframeOverride`. Built
  // from the RAW tiles so the registry keeps `externalRoutePath` for iframe
  // apps regardless of the UI `target`.
  const registryEntries: AppLoadMeta[] = loadableTiles.map((t) => ({
    appId: appIdFromRoutePath(t.tileRoutePath),
    appTypeID: t.appTypeID,
    externalRoutePath: t.externalRoutePath,
  }));
  appRegistryCache.set(clientId, registryEntries);
  setAppRegistry(registryEntries);

  if (import.meta.env.DEV) {
    console.warn(
      '[mfe] app registry populated:',
      registryEntries.map((e) => `${e.appId}=type${e.appTypeID}`).join(', '),
    );
  }

  // The launcher UI list keeps the role filter (what the user is allowed to
  // see), then maps to view models.
  const visibleTiles = loadableTiles.filter((t) =>
    allowedScreens ? allowedScreens.has(t.tileRoutePath) : true,
  );

  return visibleTiles.map(mapTile);
}

const registryInflight = new Map<string, Promise<void>>();

/**
 * Ensure the app load-registry is populated for `clientId` BEFORE an MFE mounts.
 *
 * The registry carries each app's `appTypeID` (inline MFE vs iframe vs external)
 * and `externalRoutePath`. It is otherwise populated only as a side effect of
 * the launcher's `useAppTiles`, so a direct navigation / deep link to an iframe
 * app resolves the loader with an EMPTY registry — `getAppLoadMeta` returns
 * `undefined`, the app defaults to module-federation, and iframe apps 404 on a
 * non-existent `remoteEntry.js` / `manifest.json`. Legacy avoids this by
 * awaiting `getApps(clientId)` in `resolveIframeOverride` before deciding how to
 * mount. Deduped + cached per client; best-effort (a failed fetch leaves the
 * registry empty and the loader falls back to its default path, as before).
 */
export async function ensureAppRegistry(clientId: string | undefined): Promise<void> {
  if (!clientId) return;

  const cached = appRegistryCache.get(clientId);
  if (cached) {
    setAppRegistry(cached);
    return;
  }

  // If React Query already has fresh data for this client, use it instead of
  // firing another HTTP request.
  const queryData = queryClient.getQueryData<AppTileView[]>(['app-tiles', clientId]);
  if (queryData) {
    const entries: AppLoadMeta[] = queryData.map((tile) => ({
      appId: tile.appId,
      appTypeID: tile.appTypeID,
      externalRoutePath: tile.externalRoutePath,
    }));
    appRegistryCache.set(clientId, entries);
    setAppRegistry(entries);
    return;
  }

  const existing = registryInflight.get(clientId);
  if (existing) return existing;

  const request = fetchAppTiles(clientId)
    .then((tiles) => {
      // Seed React Query cache so useAppTiles() finds data without re-fetching.
      queryClient.setQueryData(['app-tiles', clientId], tiles);
    })
    .catch(() => undefined)
    .finally(() => registryInflight.delete(clientId));
  registryInflight.set(clientId, request);
  return request;
}

const adminRegistryCache = new Map<string, AppLoadMeta[]>();
const adminRegistryInflight = new Map<string, Promise<void>>();

/**
 * Fetch the ADMIN tile feed and fold it into the load registry. Legacy keeps
 * admin apps in a separate feed (`/portaladmin/api/v1/Admin/GetTiles`, see
 * `AuthorizeService.getAdminApps`) that the workspace launcher never loads, so
 * the React shell — which only fetched workspace tiles — had no loader metadata
 * for admin apps and defaulted them to module-federation (iframe/external admin
 * apps would then 404). Merged (never replaces) so the workspace registry keeps
 * priority on a name clash.
 */
async function fetchAdminTiles(clientId: string): Promise<void> {
  const raw = await http<AppTileDto[] | { tiles?: AppTileDto[]; data?: AppTileDto[] }>(
    Endpoints.adminTiles(clientId),
  );
  const tiles: AppTileDto[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.tiles)
      ? raw.tiles
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

  // Cache raw admin tile paths for the Layer 2 tile-existence gate.
  adminTilePathsByClient.set(clientId, tilePathSet(tiles));

  const loadableTiles = tiles
    .filter((t) => !HIDDEN_APP_TYPES.has(t.appTypeID))
    .filter((t) => !TILE_HIDE_RE.test(t.tileRoutePath ?? ''));

  const entries: AppLoadMeta[] = loadableTiles.map((t) => ({
    appId: appIdFromRoutePath(t.tileRoutePath),
    appTypeID: t.appTypeID,
    externalRoutePath: t.externalRoutePath,
  }));
  adminRegistryCache.set(clientId, entries);
  mergeAppRegistry(entries);
}

/**
 * Ensure the ADMIN apps are present in the load registry for `clientId` before
 * an admin MFE mounts. Called (in addition to {@link ensureAppRegistry}) from
 * the `/{clientId}/app-container/admin/apps/{appId}` routes. Deduped + cached
 * per client; best-effort (a failed fetch leaves the admin apps out and the
 * loader falls back to its default path, as before).
 */
export async function ensureAdminAppRegistry(clientId: string | undefined): Promise<void> {
  if (!clientId) return;

  const cached = adminRegistryCache.get(clientId);
  if (cached) {
    mergeAppRegistry(cached);
    return;
  }

  const existing = adminRegistryInflight.get(clientId);
  if (existing) return existing;

  const request = fetchAdminTiles(clientId)
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => adminRegistryInflight.delete(clientId));
  adminRegistryInflight.set(clientId, request);
  return request;
}

export function useAppTiles(clientId: string | undefined) {
  const query = useQuery({
    queryKey: ['app-tiles', clientId ?? null],
    enabled: Boolean(clientId),
    queryFn: () => fetchAppTiles(clientId as string),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (!clientId) return;
    const cachedRegistry = appRegistryCache.get(clientId);
    if (cachedRegistry) {
      setAppRegistry(cachedRegistry);
      return;
    }
    if (query.data) {
      setAppRegistry(
        query.data.map((tile) => ({
          appId: tile.appId,
          appTypeID: tile.appTypeID,
          externalRoutePath: tile.externalRoutePath,
        })),
      );
    }
  }, [clientId, query.data]);

  return query;
}
